import React, { Fragment, useState } from 'react';
import { FaEdit } from 'react-icons/fa';
import { useSelector } from 'react-redux';
import { useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { TD } from 'styled/table';
import checkPermission from 'utils/checkPermission';
import permissions from 'utils/permissionConstants';
import { updateDocumentByID } from 'apis/customApiHandler';
import Edit from './Edit';

const PeopleCell = ({ users, fallback }) => {
  if (!users?.length) return <>{fallback}</>;

  return (
    <>
      {users.map((user, position) => (
        <span key={user._id || `${user.name}-${position}`}>
          {position > 0 && ', '}
          <span
            className={user.active === false ? 'text-red-600 font-semibold' : ''}
            title={
              user.active === false
                ? `${user.name} is inactive in Users`
                : undefined
            }
          >
            {user.name}
          </span>
        </span>
      ))}
    </>
  );
};

const ViewList = ({ data, index, serial }) => {
  const [showEditModel, setShowEditModel] = useState(false);
  // Hub-level lock only — fleet lock is read-only here (managed on Fleet Master).
  const [saving, setSaving] = useState(false);
  const [savingStatus, setSavingStatus] = useState(false);
  const hubCanCreateOrder = data.hubCanCreateOrder !== false;
  const hubActive = data.hubStatus !== 'Inactive';
  const queryClient = useQueryClient();
  const currentUser = useSelector((state) => state.user.currentUser);

  const canShowActions = checkPermission(
    [permissions.manage_masters, permissions.view_fleet_master_actions],
    currentUser
  );

  const fleetLocked = data.fleetCanCreateOrder === false;
  const effectivelyUnlocked = hubActive && hubCanCreateOrder && !fleetLocked;
  const lockLabel = !hubActive
    ? 'Locked (Inactive)'
    : fleetLocked
    ? 'Locked (Fleet)'
    : hubCanCreateOrder
    ? 'Unlocked'
    : 'Locked';

  return (
    <Fragment>
      <tr key={data._id} className={`${index % 2 !== 0 && 'bg-gray-100'}`}>
        <TD>{serial}</TD>
        <TD>{data?.fleetCode}</TD>
        <TD>{data?.fleetName}</TD>
        <TD>{data?.hubCode}</TD>
        <TD>{data?.hubName}</TD>
        <TD>{data?.depoCode}</TD>
        <TD>{data?.region}</TD>
        <TD>{data?.zone}</TD>
        <TD>{data?.bodyType}</TD>
        <TD>
          {data?.openingDate
            ? new Date(data.openingDate)
                .toLocaleDateString('en-GB')
                .replace(/\//g, '-')
            : ''}
        </TD>
        <TD>
          <PeopleCell
            users={data?.hubInChargeUsers}
            fallback={data?.hubInCharge}
          />
        </TD>
        <TD>
          <PeopleCell
            users={data?.fleetManagerUsers}
            fallback={data?.fleetManager}
          />
        </TD>
        <TD>
          <PeopleCell
            users={data?.clusterManagerUsers}
            fallback={data?.clusterManager}
          />
        </TD>
        <TD>
          <PeopleCell users={data?.zmUsers} fallback={data?.zm} />
        </TD>
        <TD>
          <div className="flex items-center space-x-2">
            <button
              type="button"
              role="switch"
              aria-checked={hubActive}
              disabled={!canShowActions || savingStatus}
              onClick={async () => {
                const newStatus = hubActive ? 'Inactive' : 'Active';
                setSavingStatus(true);
                const result = await updateDocumentByID(
                  'fleet-hub-mapping/hub',
                  data._id,
                  { hubStatus: newStatus }
                );
                setSavingStatus(false);
                if (result) {
                  toast.success(`Hub marked ${newStatus} successfully`);
                  queryClient.invalidateQueries('fleet-hub-mapping');
                  queryClient.invalidateQueries('fleet-hub-mapping-options');
                }
              }}
              className={`relative inline-flex items-center h-6 rounded-full w-11 transition-colors duration-300 disabled:cursor-not-allowed ${
                hubActive ? 'bg-green-500' : 'bg-gray-400'
              } ${savingStatus || !canShowActions ? 'opacity-60' : ''}`}
            >
              <span
                className={`inline-block w-4 h-4 transform bg-white rounded-full transition-transform duration-300 ${
                  hubActive ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
            <span className="text-sm font-medium whitespace-nowrap">
              {hubActive ? 'Active' : 'Inactive'}
            </span>
          </div>
        </TD>
        <TD>
          {canShowActions && !fleetLocked && hubActive ? (
            <div className="flex items-center space-x-2">
              <button
                type="button"
                disabled={saving}
                onClick={async () => {
                  const newValue = !hubCanCreateOrder;
                  setSaving(true);
                  const result = await updateDocumentByID(
                    'fleet-hub-mapping/hub',
                    data._id,
                    { canCreateOrder: newValue }
                  );
                  setSaving(false);
                  if (result) {
                    toast.success(
                      `Hub order ${newValue ? 'Unlocked' : 'Locked'} successfully`
                    );
                    queryClient.invalidateQueries('fleet-hub-mapping');
                    queryClient.invalidateQueries('fleet-hub-mapping-options');
                  }
                }}
                className={`relative inline-flex items-center h-6 rounded-full w-11 transition-colors duration-300 ${
                  effectivelyUnlocked ? 'bg-green-500' : 'bg-red-500'
                } ${saving ? 'opacity-60' : ''}`}
              >
                <span
                  className={`inline-block w-4 h-4 transform bg-white rounded-full transition-transform duration-300 ${
                    effectivelyUnlocked ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
              <span className="text-sm font-medium whitespace-nowrap">
                {lockLabel}
              </span>
            </div>
          ) : (
            <div className="flex items-center space-x-2">
              <span
                className={`relative inline-flex items-center h-6 rounded-full w-11 ${
                  effectivelyUnlocked ? 'bg-green-500' : 'bg-red-500'
                } ${fleetLocked || !hubActive ? 'opacity-60' : ''}`}
              >
                <span
                  className={`inline-block w-4 h-4 transform bg-white rounded-full ${
                    effectivelyUnlocked ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </span>
              <span className="text-sm font-medium whitespace-nowrap">
                {lockLabel}
              </span>
            </div>
          )}
        </TD>
        <TD>
          {canShowActions ? (
            <button
              type="button"
              onClick={() => setShowEditModel(true)}
              className="p-2 m-1 rounded-md bg-green-500 hover:bg-green-600 text-white"
            >
              <FaEdit />
            </button>
          ) : (
            <p>----</p>
          )}
        </TD>
      </tr>
      {showEditModel && (
        <Edit data={data} handleClick={() => setShowEditModel(false)} />
      )}
    </Fragment>
  );
};

export default ViewList;
